import useThrottledCallback from 'beautiful-react-hooks/useThrottledCallback';
import type { RefObject } from 'react';
import { useEffect } from 'react';

export type FittyOptions = {
  minSize?: number;
  maxSize?: number | 'auto';
  multiLine?: boolean;
  textOverflow?: string;
};

const defaultOptions: FittyOptions = {
  minSize: 11,
  maxSize: 'auto',
  multiLine: true,
  textOverflow: '',
};

export const useFitty = (elementRef: RefObject<HTMLElement>, options = defaultOptions) => {
  const fitElement = useThrottledCallback(
    (el: HTMLElement, options: FittyOptions) => {
      el.style.overflowX = 'clip';
      el.style.removeProperty('font-size');

      const currentFontSize = getComputedStyle(el).fontSize;
      const currentFontSizeNum = parseFloat(currentFontSize.replace('px', ''));
      const maxSize = options.maxSize === 'auto' ? currentFontSizeNum : options.maxSize;

      const ratio = el.parentElement.clientWidth / el.parentElement.scrollWidth;
      let finalFontSize = Math.floor(currentFontSizeNum * ratio);

      if (finalFontSize < options.minSize) {
        finalFontSize = options.minSize;
      } else if (finalFontSize > maxSize) {
        finalFontSize = maxSize;
      }

      el.style.fontSize = `${finalFontSize}px`;
      if (options.textOverflow) {
        el.parentElement.style.overflowX = 'clip';
        el.parentElement.style.textOverflow = options.textOverflow;
      } else {
        el.style.removeProperty('overflow-x');
      }
    },
    [],
    200,
  );

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const opt = { ...defaultOptions, ...options };

    const observer = new ResizeObserver((elements) => {
      elements.forEach((el) => {
        fitElement(el.target as HTMLElement, opt);
      });
    });

    element.style.whiteSpace = options.multiLine ? 'normal' : 'nowrap';
    element.style.width = '100%';

    observer.observe(element);
    fitElement(element, opt);

    return () => {
      observer?.disconnect();
    };
  }, [elementRef, fitElement, options]);
};
